import React, { useEffect, useState } from 'react';
import { XMarkIcon, DocumentTextIcon, ArrowDownTrayIcon, BuildingOffice2Icon, CalendarIcon, CurrencyDollarIcon, HashtagIcon, ExclamationCircleIcon, CheckCircleIcon, DocumentDuplicateIcon, CheckIcon, IdentificationIcon } from '@heroicons/react/24/outline';
import type { XmlTerceiro } from '../../types/xmlTerceiros';
import { useBaixarDanfe, useBaixarXml } from '../../hooks/useXmlTerceiros';
import { money, formatCnpj, formatDateTime } from '../../utils/format';
import { btnPrimario, btnSecundario } from '../ui/iris';

interface XmlTerceirosModalProps {
  xml: XmlTerceiro;
  isOpen: boolean;
  onClose: () => void;
}

const XmlTerceirosModal: React.FC<XmlTerceirosModalProps> = ({
  xml,
  isOpen,
  onClose,
}) => {
  const baixarDanfeMutation = useBaixarDanfe();
  const baixarXmlMutation = useBaixarXml();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Fechar com ESC
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Bloquear scroll do body
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [isOpen]);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // silencioso
    }
  };

  const handleBaixarDanfe = () => baixarDanfeMutation.mutate(xml.gid);
  const handleBaixarXml = () => baixarXmlMutation.mutate(xml.gid);

  if (!isOpen) return null;

  const cnpjFormatado = formatCnpj(xml.emitenteCnpj);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px] transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Detalhes da NF-e"
          className="relative transform overflow-hidden rounded-iris-lg bg-surface border border-line-strong text-left shadow-iris-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-line">
            <div className="flex items-center gap-3 min-w-0">
              <div className="bg-accent p-2 rounded-iris flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-bold text-tx truncate">
                  Detalhes da NF-e {xml.numero}
                </h2>
                <p className="text-sm text-tx-2">
                  Informações completas do documento fiscal
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-surface-2 rounded-iris transition-colors flex-shrink-0"
              aria-label="Fechar"
            >
              <XMarkIcon className="h-5 w-5 text-tx-3" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {/* Hero: Status + Valor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div
                className={`rounded-iris p-4 border ${
                  xml.pendente
                    ? 'bg-warning-soft border-warning-border'
                    : 'bg-ok-soft border-ok-border'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`p-2 rounded-full ${
                      xml.pendente ? 'bg-warning-soft' : 'bg-ok-soft'
                    }`}
                  >
                    {xml.pendente ? (
                      <ExclamationCircleIcon className="h-5 w-5 text-warning" />
                    ) : (
                      <CheckCircleIcon className="h-5 w-5 text-ok" />
                    )}
                  </div>
                  <div>
                    <p className="text-xs text-tx-2">Status</p>
                    <p
                      className={`text-lg font-bold ${
                        xml.pendente ? 'text-warning' : 'text-ok'
                      }`}
                    >
                      {xml.pendente ? 'Pendente' : 'Processado'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-iris p-4 border bg-ok-soft border-ok-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-full bg-ok-soft">
                    <CurrencyDollarIcon className="h-5 w-5 text-ok" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-tx-2">Valor Total</p>
                    <p className="text-lg font-bold text-ok truncate">
                      {money(xml.valorTotalNf)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Grid de informações */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Documento */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-tx flex items-center gap-2 border-b border-line pb-2">
                  <HashtagIcon className="h-5 w-5 text-tx-3" />
                  Informações do Documento
                </h3>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <HashtagIcon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-tx-2">Número da NF-e</p>
                      <p className="font-semibold text-tx">
                        {xml.numero}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <CalendarIcon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-tx-2">
                        Data/Hora de Emissão
                      </p>
                      <p className="font-semibold text-tx">
                        {formatDateTime(xml.dataHoraEmissao)}
                      </p>
                    </div>
                  </div>

                  {xml.empresa && (
                    <div className="flex items-start gap-3">
                      <BuildingOffice2Icon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-tx-2">
                          Código da Empresa
                        </p>
                        <p className="font-semibold text-tx">
                          {xml.empresa}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Emitente */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-tx flex items-center gap-2 border-b border-line pb-2">
                  <BuildingOffice2Icon className="h-5 w-5 text-tx-3" />
                  Emitente
                </h3>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <BuildingOffice2Icon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <p className="text-sm text-tx-2">Razão Social</p>
                      <p className="font-semibold text-tx break-words">
                        {xml.emitenteNome || 'Não informado'}
                      </p>
                    </div>
                  </div>

                  {xml.emitenteFantasia &&
                    xml.emitenteFantasia !== xml.emitenteNome && (
                      <div className="flex items-start gap-3">
                        <BuildingOffice2Icon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <p className="text-sm text-tx-2">Nome Fantasia</p>
                          <p className="font-semibold text-tx break-words">
                            {xml.emitenteFantasia}
                          </p>
                        </div>
                      </div>
                    )}

                  <div className="flex items-start gap-3">
                    <IdentificationIcon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-tx-2">CNPJ</p>
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-tx">
                          {cnpjFormatado}
                        </p>
                        <button
                          onClick={() => handleCopy(cnpjFormatado, 'cnpj')}
                          className="p-1 hover:bg-surface-2 rounded transition-colors"
                          title="Copiar CNPJ"
                        >
                          {copiedKey === 'cnpj' ? (
                            <CheckIcon className="h-3.5 w-3.5 text-ok" />
                          ) : (
                            <DocumentDuplicateIcon className="h-3.5 w-3.5 text-tx-3" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {xml.emitenteIe && (
                    <div className="flex items-start gap-3">
                      <HashtagIcon className="h-5 w-5 text-tx-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-tx-2">
                          Inscrição Estadual
                        </p>
                        <p className="font-mono text-tx">
                          {xml.emitenteIe}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Chave de Acesso */}
            {xml.id && (
              <div className="mt-6 bg-accent-soft border border-line p-4 rounded-iris">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-tx">
                    Chave de Acesso
                  </h4>
                  <button
                    onClick={() => handleCopy(xml.id!, 'chave')}
                    className="inline-flex items-center gap-1.5 text-xs text-accent-text hover:opacity-80 transition-opacity"
                  >
                    {copiedKey === 'chave' ? (
                      <>
                        <CheckIcon className="h-3.5 w-3.5" />
                        Copiada
                      </>
                    ) : (
                      <>
                        <DocumentDuplicateIcon className="h-3.5 w-3.5" />
                        Copiar
                      </>
                    )}
                  </button>
                </div>
                <p className="font-mono text-sm text-tx-2 break-all">
                  {xml.id}
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-surface-2 px-6 py-4 flex flex-col-reverse sm:flex-row sm:justify-end gap-3 border-t border-line">
            <button onClick={onClose} className={btnSecundario}>
              Fechar
            </button>
            <button
              onClick={handleBaixarXml}
              disabled={baixarXmlMutation.isPending}
              className="inline-flex items-center justify-center gap-1.5 h-9 px-4 text-sm font-semibold rounded-iris bg-ok text-white hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <DocumentTextIcon className="h-4 w-4" />
              {baixarXmlMutation.isPending ? 'Preparando...' : 'Baixar XML'}
            </button>
            <button
              onClick={handleBaixarDanfe}
              disabled={baixarDanfeMutation.isPending}
              className={btnPrimario}
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {baixarDanfeMutation.isPending ? 'Gerando...' : 'Baixar DANFE'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default XmlTerceirosModal;
